
import React, { useRef, useState } from "react";
import Link from "next/link";
import { useEffect } from "react";
import { isEmpty } from "lodash";
import Image from 'next/image';
import styles from './homesearchresults.module.css'
import { avenirNextRegular, avenirNextMedium } from '@/styles/fonts'
import clsx from 'clsx';


type AdThumbnail = {
  src: string;
  thumbnail: string;
};
type Advertiser = {
  advertiser: string,
  advertiserName: string,
  profileUrl: string,
};
type Advertisers = Array<Advertiser>;

export default function HomeSearchResults(props: {
  advertisers: Advertisers,
  activeIndex: number,
  setActiveIndex: (index: number) => void,
}) {
  const { advertisers } = props;
  const [topAdsData, setTopAdsData] = useState<{
    [key: string]: AdThumbnail[]
  }>({});
  const [isVisible, setIsVisible] = useState(true);

  const dropdown = useRef<HTMLDivElement>(null);


  // https://react.dev/reference/react/useEffect#connecting-to-an-external-system
  useEffect(() => {
    // addEventListener returns listener removal function to run on component unmount
    return window.addEventListener('click', (e) => {
      const searchResults = (dropdown.current as unknown) as HTMLDivElement;
      if (searchResults && !searchResults.contains(e.target as Node)) {
        setIsVisible(false);
      }
    });
  }, []);

  useEffect(() => {
    const controllers: AbortController[] = [];
    const isFetchComplete: boolean[] = [];
    const getTopAds = async (advertiser: string) => {
      const controller = new AbortController();
      controllers.push(controller);
      isFetchComplete.push(false);
      const fetchId = isFetchComplete.length - 1;
      const signal = controller.signal;

      try {
        const response = await fetch(`/free-ui-api/top-images/advertiser/${advertiser}/3`, { signal });
        if (!response.ok) {
          isFetchComplete[fetchId] = true;
          return;
        }
        const data = await response.json();
        setTopAdsData((prev) => ({
          ...prev,
          [advertiser]: data,
        }));
        isFetchComplete[fetchId] = true;
      } catch (error) {
        isFetchComplete[fetchId] = true;
        if ((error as string) === 'fetch cancelled') {
          return;
        } else {
          throw error;
        }
      }
    }

    advertisers.forEach((advertiser) => {
      getTopAds(advertiser.advertiser);
    });

    return () => {
      controllers.forEach((controller, idx) => {
        if (!isFetchComplete[idx]) {
          controller.abort('fetch cancelled');
        }
      })
    };
  }, [advertisers]);

  if (!isVisible) {
    return null;
  }

  return (
    <div ref={dropdown} className={clsx(avenirNextRegular.className,
      "absolute text-[16px] text-[#43484d] shadow-md bg-white rounded-md left-0 right-0 py-[30px] px-[30px] m-[8px] z-50",
      "top-[50px] sm:top-[70px] md:top-[90px] lg:top-[100px]")}>

      <div className="rounded-md border-[1px] border-[#ddd]">
        {advertisers.map((advertiser: Advertiser, index: number) => (
          <Link key={index} href={advertiser.profileUrl}
            data-testid={"advertiser-link-" + index}
            onMouseOver={() => {
              props.setActiveIndex(index);
            }}
            //box-shadow inset 0 0 0 1px #2da1f8
            // TODO: activeIndex == index condition
            className={clsx("flex flex-row flex-nowrap w-full py-[10px] px-[15px]",
              props.activeIndex == index && "[box-shadow:_inset_0_0_0_1px_#2da1f8]",
              index < advertisers.length - 1 && "border-b-[1px] border-[#ddd]")}>
            <div className="truncate text-black my-auto w-full" >
              {advertiser.advertiser}
            </div>

            <div className='w-[145px] min-w-[145px] max-w-[145px] flex'>

              {isEmpty(topAdsData[advertiser.advertiser]) && (
                [1, 2, 3].map((index) => (
                  <div key={index} className={clsx("m-[4px] outline outline-1 outline-[#ddd] ",
                    "relative w-[40px] h-[40px]"
                  )}>
                    <div className={clsx("", styles.loadingthumbnail)}>
                      <div className={clsx(styles.circleG, styles.circleG1)}></div>
                      <div className={clsx(styles.circleG, styles.circleG2)}></div>
                      <div className={clsx(styles.circleG, styles.circleG3)}></div>
                    </div>
                  </div>
                ))
              )}
              {topAdsData[advertiser.advertiser] && topAdsData[advertiser.advertiser].map(
                (ad: AdThumbnail, index) => (

                  <Image key={ad.src + " " + index}
                    className={clsx("m-[4px] inline outline outline-1 outline-[#ddd]")}
                    src={ad.thumbnail}
                    width={40} height={40} alt={"-"} />
                ))}
            </div>
          </Link>
        ))}
      </div>
      <div className="w-full text-center mt-[20px] ">
        <Link role="button"
          className={`${avenirNextMedium.className} mx-auto rounded bg-[#e46700] py-[9px] px-[22px] text-[18px] text-white hover:text-black`}
          href={advertisers.length ? advertisers[0].profileUrl : "/"}>Adbeat Search</Link>
      </div>
    </div>
  )
}